import { useState, useEffect, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import HeaderMaster from '../components/HeaderMaster';
import SideBarMaster from '../components/SideBarMaster';
import FooterNavigationMaster from '../components/FooterNavigationMaster';
import styles from './EquipeMaster.module.css';
import { MdPeople, MdSearch, MdDelete, MdOutlineMarkEmailRead, MdPerson, MdAdd, MdEdit, MdBlock } from "react-icons/md";
import { fetchWithAuth } from '../../../shared/utils/auth';
import { EstabelecimentosContext } from '../../../shared/contexts/EstabelecimentosContext';

// Gestão de Equipe/Acesso: visão de todos os atendentes (Equipe) de todos os
// estabelecimentos, com status calculado (ativo / pendente de ativação /
// convite expirado). Sem isso, um convite de ativação perdido (link expirado
// antes da pessoa clicar, email caído no spam etc.) ficava invisível — o
// master só descobria quando o admin da loja reclamasse que o funcionário
// "sumiu".
function EquipeMaster() {
    const [equipe, setEquipe] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('todos');
    const [reenviando, setReenviando] = useState(null);
    const [searchParams] = useSearchParams();

    const { todosEstabelecimentos, fetchTodosEstabelecimentos } = useContext(EstabelecimentosContext);
    const [mostrarFormNovo, setMostrarFormNovo] = useState(false);
    const [funcionarioEditando, setFuncionarioEditando] = useState(null);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3002';

    useEffect(() => {
        fetchTodosEstabelecimentos();
    }, []);

    const fetchEquipe = async () => {
        try {
            setLoading(true);
            const res = await fetchWithAuth(`${API_URL}/master/equipe`);
            if (!res) return; // fetchWithAuth já tratou sessão expirada

            if (!res.ok) {
                throw new Error('Erro ao buscar equipe');
            }

            const data = await res.json();
            setEquipe(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Erro ao buscar equipe:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEquipe();
    }, []);

    const handleReenviarConvite = async (id, nome) => {
        if (!window.confirm(`Reenviar o email de ativação para ${nome}?`)) return;

        try {
            setReenviando(id);
            const res = await fetchWithAuth(`${API_URL}/master/equipe/${id}/reenviar-convite`, {
                method: 'POST'
            });

            if (!res) return;

            const data = await res.json().catch(() => ({}));

            if (!res.ok) {
                alert(data.error || 'Erro ao reenviar convite.');
                return;
            }

            alert('Convite reenviado com sucesso!');
            fetchEquipe();
        } catch (error) {
            console.error('Erro ao reenviar convite:', error);
            alert('Erro ao reenviar convite. Tente novamente.');
        } finally {
            setReenviando(null);
        }
    };

    const handleExcluir = async (id, nome) => {
        if (!window.confirm(`Excluir o cadastro de ${nome} da equipe? Essa ação não pode ser desfeita.`)) return;

        try {
            const res = await fetchWithAuth(`${API_URL}/master/usuarios/${id}?tipo=atendente`, {
                method: 'DELETE'
            });

            if (!res) return;

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                alert(data.error || 'Erro ao excluir.');
                return;
            }

            fetchEquipe();
        } catch (error) {
            console.error('Erro ao excluir funcionário:', error);
            alert('Erro ao excluir. Tente novamente.');
        }
    };

    const handleDesativar = async (id, nome) => {
        if (!window.confirm(`Desativar o acesso de ${nome}? Ele(a) não vai mais conseguir logar.`)) return;

        try {
            const res = await fetchWithAuth(`${API_URL}/funcionarios/${id}/desativar`, { method: 'PUT' });
            if (!res) return;

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(data.error || 'Erro ao desativar.');
                return;
            }

            fetchEquipe();
        } catch (error) {
            console.error('Erro ao desativar funcionário:', error);
            alert('Erro ao desativar. Tente novamente.');
        }
    };

    const filteredEquipe = equipe.filter(item => {
        const matchSearch =
            item.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.nome_estabelecimento?.toLowerCase().includes(searchTerm.toLowerCase());

        const matchStatus = filterStatus === 'todos' || item.status === filterStatus;

        return matchSearch && matchStatus;
    });

    const getStatusBadge = (status) => {
        const badges = {
            ativo: <span className={styles.badgeAtivo}>Ativo</span>,
            pendente: <span className={styles.badgePendente}>Pendente</span>,
            expirado: <span className={styles.badgeExpirado}>Convite expirado</span>
        };
        return badges[status] || <span className={styles.badgePendente}>{status}</span>;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('pt-BR');
    };

    return (
        <div className={styles.secaoPrincipal}>
            <SideBarMaster />
            <div className={styles.containerDashboard}>
                <HeaderMaster />

                <div className={styles.contentDashboard}>
                    <div className={styles.tituloSecao}>
                        <div>
                            <h2>Gestão de Equipe/Acesso</h2>
                        </div>
                    </div>

                    <div className={styles.acoesBar}>
                        <button className={styles.btnAcaoPrimaria} onClick={() => setMostrarFormNovo(true)}>
                            <MdAdd size={18} /> Novo Funcionário
                        </button>
                    </div>

                    <div className={styles.filtersContainer}>
                        <div className={styles.searchBar}>
                            <MdSearch className={styles.searchIcon} />
                            <input
                                type="text"
                                placeholder="Pesquisar por nome, email ou estabelecimento..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                        </div>

                        <div className={styles.statusFilter}>
                            <button
                                className={filterStatus === 'todos' ? styles.filterBtnActive : styles.filterBtn}
                                onClick={() => setFilterStatus('todos')}
                            >
                                Todos
                            </button>
                            <button
                                className={filterStatus === 'ativo' ? styles.filterBtnActive : styles.filterBtn}
                                onClick={() => setFilterStatus('ativo')}
                            >
                                Ativos
                            </button>
                            <button
                                className={filterStatus === 'pendente' ? styles.filterBtnActive : styles.filterBtn}
                                onClick={() => setFilterStatus('pendente')}
                            >
                                Pendentes
                            </button>
                            <button
                                className={filterStatus === 'expirado' ? styles.filterBtnActive : styles.filterBtn}
                                onClick={() => setFilterStatus('expirado')}
                            >
                                Expirados
                            </button>
                        </div>
                    </div>

                    <div className={styles.statsCards}>
                        <div className={styles.statCard}>
                            <h3>Total de Atendentes</h3>
                            <p className={styles.statNumber}>{equipe.length}</p>
                        </div>
                        <div className={styles.statCard}>
                            <h3>Ativos</h3>
                            <p className={styles.statNumber} style={{ color: 'var(--sh-live)' }}>
                                {equipe.filter(e => e.status === 'ativo').length}
                            </p>
                        </div>
                        <div className={styles.statCard}>
                            <h3>Pendentes de Ativação</h3>
                            <p className={styles.statNumber} style={{ color: 'var(--sh-pending)' }}>
                                {equipe.filter(e => e.status === 'pendente').length}
                            </p>
                        </div>
                        <div className={styles.statCard}>
                            <h3>Convites Expirados</h3>
                            <p className={styles.statNumber} style={{ color: 'var(--sh-off)' }}>
                                {equipe.filter(e => e.status === 'expirado').length}
                            </p>
                        </div>
                    </div>

                    <div className={styles.tableContainer}>
                        {loading ? (
                            <p className={styles.loading}>Carregando...</p>
                        ) : (
                            <>
                                {/* Tabela para desktop */}
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Nome</th>
                                            <th>Email</th>
                                            <th>Estabelecimento</th>
                                            <th>Status</th>
                                            <th>Convite enviado em</th>
                                            <th>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEquipe.length > 0 ? (
                                            filteredEquipe.map(item => (
                                                <tr key={item.id}>
                                                    <td className={styles.nomeUsuario}>
                                                        <MdPerson className={styles.iconTable} />
                                                        {item.nome}
                                                    </td>
                                                    <td>{item.email || 'N/A'}</td>
                                                    <td>{item.nome_estabelecimento}</td>
                                                    <td>{getStatusBadge(item.status)}</td>
                                                    <td>{formatDate(item.data_convite)}</td>
                                                    <td>
                                                        <div className={styles.actionButtons}>
                                                            {item.status !== 'ativo' && (
                                                                <button
                                                                    className={styles.btnResend}
                                                                    onClick={() => handleReenviarConvite(item.id, item.nome)}
                                                                    disabled={reenviando === item.id}
                                                                    title="Reenviar convite de ativação"
                                                                >
                                                                    <MdOutlineMarkEmailRead />
                                                                </button>
                                                            )}
                                                            <button
                                                                className={styles.btnEdit}
                                                                onClick={() => setFuncionarioEditando(item)}
                                                                title="Editar"
                                                            >
                                                                <MdEdit />
                                                            </button>
                                                            {item.status === 'ativo' && (
                                                                <button
                                                                    className={styles.btnDelete}
                                                                    onClick={() => handleDesativar(item.id, item.nome)}
                                                                    title="Desativar acesso"
                                                                >
                                                                    <MdBlock />
                                                                </button>
                                                            )}
                                                            <button
                                                                className={styles.btnDelete}
                                                                onClick={() => handleExcluir(item.id, item.nome)}
                                                                title="Excluir"
                                                            >
                                                                <MdDelete />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="6" className={styles.noData}>
                                                    Nenhum atendente encontrado
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>

                                {/* Cards para mobile */}
                                <div className={styles.cardsContainer}>
                                    {filteredEquipe.length > 0 ? (
                                        filteredEquipe.map(item => (
                                            <div key={item.id} className={styles.card}>
                                                <div className={styles.cardHeader}>
                                                    <div className={styles.cardTitle}>
                                                        <MdPeople className={styles.cardIcon} />
                                                        <h3>{item.nome}</h3>
                                                    </div>
                                                    {getStatusBadge(item.status)}
                                                </div>

                                                <div className={styles.cardBody}>
                                                    <div className={styles.cardRow}>
                                                        <strong>Email:</strong>
                                                        <span>{item.email || 'N/A'}</span>
                                                    </div>
                                                    <div className={styles.cardRow}>
                                                        <strong>Estabelec.:</strong>
                                                        <span>{item.nome_estabelecimento}</span>
                                                    </div>
                                                    <div className={styles.cardRow}>
                                                        <strong>Convite em:</strong>
                                                        <span>{formatDate(item.data_convite)}</span>
                                                    </div>
                                                </div>

                                                <div className={styles.cardFooter}>
                                                    {item.status !== 'ativo' && (
                                                        <button
                                                            className={styles.btnResend}
                                                            onClick={() => handleReenviarConvite(item.id, item.nome)}
                                                            disabled={reenviando === item.id}
                                                        >
                                                            <MdOutlineMarkEmailRead />
                                                            <span>Reenviar</span>
                                                        </button>
                                                    )}
                                                    <button className={styles.btnEdit} onClick={() => setFuncionarioEditando(item)}>
                                                        <MdEdit />
                                                        <span>Editar</span>
                                                    </button>
                                                    <button
                                                        className={styles.btnDelete}
                                                        onClick={() => handleExcluir(item.id, item.nome)}
                                                    >
                                                        <MdDelete />
                                                        <span>Excluir</span>
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className={styles.noData}>Nenhum atendente encontrado</p>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {mostrarFormNovo && (
                <FormNovoFuncionario
                    todosEstabelecimentos={todosEstabelecimentos}
                    estabelecimentoIdPadrao={searchParams.get('estabelecimento_id') || ''}
                    onClose={() => setMostrarFormNovo(false)}
                    onSucesso={() => {
                        setMostrarFormNovo(false);
                        fetchEquipe();
                    }}
                />
            )}

            {funcionarioEditando && (
                <FormEditarFuncionario
                    funcionario={funcionarioEditando}
                    onClose={() => setFuncionarioEditando(null)}
                    onSucesso={() => {
                        setFuncionarioEditando(null);
                        fetchEquipe();
                    }}
                />
            )}

            <FooterNavigationMaster />
        </div>
    );
}

const API_URL_MODAIS = process.env.REACT_APP_API_URL || 'http://localhost:3002';

// ===================== Formulário: novo funcionário ===================== //
// Master escolhe pra qual estabelecimento é (admin comum não escolhe — o
// dele já vem implícito do login), mesmo endpoint que CadastroGarcons.jsx usa.
function FormNovoFuncionario({ todosEstabelecimentos, estabelecimentoIdPadrao, onClose, onSucesso }) {
    const [estabelecimentoId, setEstabelecimentoId] = useState(estabelecimentoIdPadrao);
    const [nome, setNome] = useState('');
    const [email, setEmail] = useState('');
    const [telefone, setTelefone] = useState('');
    const [tipoUsuario, setTipoUsuario] = useState('atendente');
    const [salvando, setSalvando] = useState(false);

    const estabelecimentoSelecionado = todosEstabelecimentos.find((e) => String(e.id) === String(estabelecimentoId));

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!estabelecimentoId || !nome.trim() || !email.trim()) {
            alert('Selecione o estabelecimento e preencha nome e email.');
            return;
        }

        try {
            setSalvando(true);

            const res = await fetchWithAuth(`${API_URL_MODAIS}/cadastrogarcons`, {
                method: 'POST',
                body: JSON.stringify({
                    nome: nome.trim(),
                    email: email.trim(),
                    telefone: telefone.trim(),
                    tipo_usuario: tipoUsuario,
                    estabelecimento_id: estabelecimentoId,
                    id_administrador: estabelecimentoSelecionado?.id_admin || null
                })
            });

            if (!res) return;

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(data.error || 'Erro ao cadastrar funcionário.');
                return;
            }

            alert('Funcionário cadastrado! Um email de ativação foi enviado.');
            onSucesso();
        } catch (error) {
            alert(`Erro na requisição: ${error.message}`);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button className={styles.modalClose} onClick={onClose}>×</button>
                <h3 className={styles.modalTitle}>Novo Funcionário</h3>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label>Estabelecimento</label>
                        <select value={estabelecimentoId} onChange={(e) => setEstabelecimentoId(e.target.value)}>
                            <option value="">Selecione...</option>
                            {todosEstabelecimentos.map((est) => (
                                <option key={est.id} value={est.id}>{est.nome_lanchonete}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.formGroup}>
                        <label>Nome</label>
                        <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Telefone</label>
                        <input type="text" value={telefone} onChange={(e) => setTelefone(e.target.value)} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Função</label>
                        <select value={tipoUsuario} onChange={(e) => setTipoUsuario(e.target.value)}>
                            <option value="atendente">Atendente/Garçom</option>
                            <option value="cozinha">Cozinha</option>
                            <option value="entregador">Entregador</option>
                        </select>
                    </div>

                    <button className={styles.btnSalvar} type="submit" disabled={salvando}>
                        {salvando ? 'Salvando...' : 'Cadastrar funcionário'}
                    </button>
                </form>
            </div>
        </div>
    );
}

// ===================== Formulário: editar funcionário ===================== //
function FormEditarFuncionario({ funcionario, onClose, onSucesso }) {
    const [nome, setNome] = useState(funcionario.nome || '');
    const [email, setEmail] = useState(funcionario.email || '');
    const [telefone, setTelefone] = useState(funcionario.telefone || '');
    const [salvando, setSalvando] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            setSalvando(true);

            const res = await fetchWithAuth(`${API_URL_MODAIS}/funcionario/${funcionario.id}`, {
                method: 'PUT',
                body: JSON.stringify({ nome, email, telefone })
            });

            if (!res) return;

            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                alert(data.error || 'Erro ao atualizar funcionário.');
                return;
            }

            alert('Funcionário atualizado!');
            onSucesso();
        } catch (error) {
            alert(`Erro na requisição: ${error.message}`);
        } finally {
            setSalvando(false);
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                <button className={styles.modalClose} onClick={onClose}>×</button>
                <h3 className={styles.modalTitle}>Editar Funcionário</h3>

                <form onSubmit={handleSubmit} className={styles.form}>
                    <div className={styles.formGroup}>
                        <label>Nome</label>
                        <input type="text" value={nome} onChange={(e) => setNome(e.target.value)} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Email</label>
                        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>Telefone</label>
                        <input type="text" value={telefone} onChange={(e) => setTelefone(e.target.value)} />
                    </div>

                    <button className={styles.btnSalvar} type="submit" disabled={salvando}>
                        {salvando ? 'Salvando...' : 'Salvar alterações'}
                    </button>
                </form>
            </div>
        </div>
    );
}

export default EquipeMaster;
